import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";
import { toast } from "sonner";
import { Plus, Buildings, UserCircle, Trash } from "@phosphor-icons/react";

/**
 * MandateRepresentatives — página de listagem/criação de representantes
 * (deputado, senador, vereador, gestor público). Suporta tanto um
 * único representante quanto estrutura institucional com N representantes.
 *
 * Fluxo:
 *   • Lista representantes do tenant (GET /api/mandate/representatives).
 *   • Modal "Novo representante" cria via POST.
 *   • Clique em representante → navega para a KB daquele representante.
 */
const ROLE_OPTIONS = [
  { value: "deputado_federal", label: "Deputado(a) Federal" },
  { value: "senador", label: "Senador(a)" },
  { value: "deputado_estadual", label: "Deputado(a) Estadual" },
  { value: "vereador", label: "Vereador(a)" },
  { value: "prefeito", label: "Prefeito(a)" },
  { value: "governador", label: "Governador(a)" },
  { value: "gestor_publico", label: "Gestor(a) Público(a)" },
  { value: "outro", label: "Outro" },
];

export default function MandateRepresentatives() {
  const nav = useNavigate();
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    name: "",
    role: "deputado_federal",
    uf: "",
    municipio: "",
    party: "",
    office_level: "federal",
    bio: "",
    aliases: "",
    official_channels: { whatsapp: "", email: "", site: "" },
    active: true,
  });

  async function reload() {
    setLoading(true);
    try {
      const { data } = await api.get("/mandate/representatives");
      setReps(data?.representatives || []);
    } catch (e) {
      toast.error("Não foi possível carregar representantes.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { reload(); }, []);

  async function submit() {
    if (!form.name || form.name.length < 2) {
      toast.error("Informe o nome do representante.");
      return;
    }
    const payload = {
      ...form,
      aliases: form.aliases.split(",").map((a) => a.trim()).filter(Boolean),
    };
    try {
      await api.post("/mandate/representatives", payload);
      toast.success("Representante cadastrado.");
      setShowNew(false);
      setForm({ ...form, name: "", bio: "", aliases: "" });
      await reload();
    } catch (e) {
      toast.error("Não foi possível cadastrar.");
    }
  }

  async function toggleActive(rep) {
    try {
      await api.patch(`/mandate/representatives/${rep.id}`, {
        ...rep, active: !rep.active,
      });
      await reload();
    } catch {
      toast.error("Falha ao atualizar.");
    }
  }

  return (
    <div className="space-y-6" data-testid="mandate-representatives-page">
      <Topbar title="Representantes" subtitle="Cadastre deputados, senadores, vereadores ou gestores públicos. Cada um com seu próprio conhecimento." />

      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">
          {loading
            ? "Carregando..."
            : `${reps.length} representante${reps.length === 1 ? "" : "s"} cadastrado${reps.length === 1 ? "" : "s"}`}
        </div>
        <button
          data-testid="mandate-new-rep-btn"
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
        >
          <Plus size={16} weight="bold" /> Novo representante
        </button>
      </div>

      {reps.length === 0 && !loading ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <Buildings size={32} className="mx-auto mb-3 text-slate-400" />
          <p className="text-slate-800 font-medium">
            Nenhum representante cadastrado ainda.
          </p>
          <p className="text-sm text-slate-500 mt-1">
            Cadastre o primeiro para subir a base de conhecimento dele (projetos, agenda, discursos, entregas).
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reps.map((r) => (
            <div
              key={r.id}
              data-testid={`mandate-rep-card-${r.id}`}
              className="rounded-xl border border-slate-200 bg-white p-4 hover:shadow-md transition"
            >
              <div className="flex items-start gap-3">
                <div className="rounded-full bg-slate-100 p-2">
                  <UserCircle size={28} className="text-slate-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-slate-900 font-semibold truncate">{r.name}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-wide mt-0.5">
                    {ROLE_OPTIONS.find((o) => o.value === r.role)?.label || r.role}
                    {r.uf ? ` · ${r.uf}` : ""}
                    {r.party ? ` · ${r.party}` : ""}
                  </div>
                  {r.bio ? (
                    <p className="text-sm text-slate-600 mt-2 line-clamp-2">{r.bio}</p>
                  ) : null}
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <Link
                  to={`/app/mandato/representantes/${r.id}/conhecimento`}
                  className="text-sm text-slate-900 underline hover:no-underline"
                  data-testid={`mandate-rep-open-kb-${r.id}`}
                >
                  Base de conhecimento
                </Link>
                <span className="text-slate-300">·</span>
                <button
                  onClick={() => toggleActive(r)}
                  className="text-sm text-slate-600 hover:text-slate-900"
                  data-testid={`mandate-rep-toggle-${r.id}`}
                >
                  {r.active ? "Pausar" : "Reativar"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Novo Representante */}
      {showNew ? (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-xl rounded-xl bg-white p-6 space-y-4" data-testid="mandate-new-rep-modal">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Novo representante</h3>
              <button onClick={() => setShowNew(false)} className="text-slate-500 hover:text-slate-900">✕</button>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <input
                data-testid="mandate-rep-name-input"
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                placeholder="Nome completo (ex: João da Silva)"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-3">
                <select
                  data-testid="mandate-rep-role-select"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="rounded border border-slate-300 px-3 py-2 text-sm"
                >
                  {ROLE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <input
                  className="rounded border border-slate-300 px-3 py-2 text-sm"
                  placeholder="UF (ex: SP)"
                  value={form.uf}
                  onChange={(e) => setForm({ ...form, uf: e.target.value.toUpperCase().slice(0, 2) })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input
                  className="rounded border border-slate-300 px-3 py-2 text-sm"
                  placeholder="Município (opcional)"
                  value={form.municipio}
                  onChange={(e) => setForm({ ...form, municipio: e.target.value })}
                />
                <input
                  className="rounded border border-slate-300 px-3 py-2 text-sm"
                  placeholder="Partido (opcional — apenas informativo)"
                  value={form.party}
                  onChange={(e) => setForm({ ...form, party: e.target.value.toUpperCase() })}
                />
              </div>
              <textarea
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                placeholder="Breve biografia pública (sem conteúdo eleitoral)"
                rows={3}
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
              />
              <input
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                placeholder="Apelidos / variantes do nome, separados por vírgula (ex: Joãozinho, Zé, J. Silva)"
                value={form.aliases}
                onChange={(e) => setForm({ ...form, aliases: e.target.value })}
              />
              <div className="text-xs text-slate-500 -mt-1">
                O Agentus usa esses nomes para identificar sobre qual representante o cidadão está falando.
              </div>
              <div className="grid grid-cols-3 gap-3">
                <input
                  className="rounded border border-slate-300 px-3 py-2 text-sm"
                  placeholder="WhatsApp oficial"
                  value={form.official_channels.whatsapp}
                  onChange={(e) => setForm({ ...form, official_channels: { ...form.official_channels, whatsapp: e.target.value } })}
                />
                <input
                  className="rounded border border-slate-300 px-3 py-2 text-sm"
                  placeholder="E-mail oficial"
                  value={form.official_channels.email}
                  onChange={(e) => setForm({ ...form, official_channels: { ...form.official_channels, email: e.target.value } })}
                />
                <input
                  className="rounded border border-slate-300 px-3 py-2 text-sm"
                  placeholder="Site oficial"
                  value={form.official_channels.site}
                  onChange={(e) => setForm({ ...form, official_channels: { ...form.official_channels, site: e.target.value } })}
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button onClick={() => setShowNew(false)} className="rounded px-3 py-2 text-sm text-slate-600 hover:bg-slate-100">Cancelar</button>
              <button
                data-testid="mandate-rep-save-btn"
                onClick={submit}
                className="rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
              >
                Cadastrar
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
